import SearchIcon from '@/components/Icons/SearchIcon';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { GREY } from '@/constants/color';
import { useAppSelector } from '@/store/hooks';
import { selectSharedApiPackages } from '@/store/selectors/sharedApi.selectors';
import { FormLabel, Radio, RadioGroup } from '@mui/material';
import debounce from 'debounce';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import PackageListItem from './PackageListItem';
import PackagePublisherItem from './PackagePublisherItem';

interface IProps {
  fieldName: string;
  id?: string;
}

const PackagesSection = ({ fieldName, id }: IProps) => {
  const { control } = useFormContext();
  const { packages, loading } = useAppSelector(selectSharedApiPackages);

  const [search, setSearch] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const selectedPackageId = useWatch({ control, name: fieldName });

  const selectedPackagePublishers = useMemo(
    () => packages.find(pkg => pkg.id === Number(selectedPackageId))?.packagePublishers ?? [],
    [packages, selectedPackageId],
  );

  const displayedPackages = useMemo(
    () => (searchTerm ? packages.filter(pkg => pkg.name.toLowerCase().includes(searchTerm)) : packages),
    [packages, searchTerm],
  );

  //#region useCallback
  const updateSearchTerm = useCallback(
    debounce((newSearch: string) => {
      setSearchTerm(newSearch.trim().toLowerCase());
    }, 200),
    [],
  );

  const onSearchValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newSearch = e.target.value || '';
      setSearch(newSearch);
      updateSearchTerm(newSearch);
    },
    [updateSearchTerm],
  );
  //#endregion

  if (!packages.length) {
    return null;
  }

  return (
    <div>
      <FormLabel
        htmlFor={id}
        sx={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center' }}
      >
        <span>Packages</span>
      </FormLabel>
      <StyledInput
        id={id}
        fullWidth
        startAdornment={<SearchIcon className={`text-[${GREY}]`} />}
        placeholder='Type to search'
        value={search}
        onChange={onSearchValueChange}
        sx={{ padding: '0 7px 0 16px', marginBottom: '8px' }}
        disabled={loading}
      />
      <div className='flex flex-col border border-[#E8E8E8] rounded-md'>
        <div className='w-full flex bg-[#E8E8E8]/40 rounded-t-lg'>
          <p className='flex-1 px-4 py-2 border-r font-[Manrope] text-[14px] font-semibold text-gray-600'>Packages</p>
          <p className='flex-1 px-4 py-2 font-[Manrope] text-[14px] font-semibold text-gray-600'>Publishers</p>
        </div>
        <div className='flex max-h-72'>
          <Controller
            name={fieldName}
            control={control}
            render={({ field }) =>
              displayedPackages.length ? (
                <RadioGroup
                  {...field}
                  value={field.value ?? ''}
                  sx={{ flex: 1, padding: '8px', overflowY: 'auto', flexWrap: 'nowrap' }}
                  className='border-r'
                >
                  {displayedPackages.map(pkg => (
                    <Radio
                      sx={{ width: '100%', padding: 0 }}
                      value={pkg.id}
                      key={pkg.id}
                      checkedIcon={<PackageListItem name={pkg.name} checked />}
                      icon={<PackageListItem name={pkg.name} />}
                      onClick={() => {
                        if (Number(field.value) === pkg.id) {
                          field.onChange(null);
                        }
                      }}
                      disableRipple
                      disabled={loading}
                    />
                  ))}
                </RadioGroup>
              ) : (
                <div className='flex-1 p-4 border-r font-[Manrope] text-[16px] text-[#303030]'>No packages</div>
              )
            }
          />
          <div className='flex-1 p-2 overflow-y-auto'>
            <ul>
              {selectedPackagePublishers.map(publisher => (
                <PackagePublisherItem key={publisher.id} name={publisher.name} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagesSection;
